import * as THREE from "three"
import { pickHandleAtPointer } from "../helpers/hover-tooltip.helper"
import {
  nearestEnd,
  projectPointToArcFrac,
  arcFracToMeters,
} from "../helpers/segment-gesture.helper"

/**
 * Controller del GESTO de avance INLINE sobre el canvas Three.js (engine-adjacent,
 * mismo patrón que HoverTooltipController / AdvanceOverlay).
 *
 * POSEE el raycaster, el Vector2 del pointer, un THREE.Plane auxiliar y el binding
 * de eventos del canvas — todo fuera de la reactividad de Vue (ver feedback vue2 +
 * three: no meter refs ni estructuras THREE en data()). El host le inyecta por
 * callbacks lo suyo: qué grupos intersectar (getTargets), el contexto de negocio de
 * un handle (getContext) y la recepción del gesto (onGesture).
 *
 * MODELO DE GESTO (validado en prototipo):
 *   - Click SIN arrastre sobre un segmento → selecciona el punto de inicio A/B
 *     (extremo más cercano). Solo fija el sentido, no marca avance.      → phase 'select'
 *   - Arrastre → la 1ª vez define el sentido (según el extremo donde arrancó) y
 *     marca el delta; mientras se arrastra emite 'preview'; al soltar 'commit'.
 *   - El sentido queda BLOQUEADO si el contexto lo indica (ya hubo avance): no se
 *     puede cambiar, todo arrastre marca en ese sentido.
 *
 * Todas las coords internas se manejan en marco ESCENA (vertices − origin). La
 * matemática de arco es invariante a la traslación, así que sentido/fracción/metros
 * salen idénticos; el host repinta con los vertices crudos + origin por su cuenta.
 */
export class InlineAdvanceController {
  /**
   * @param {object} cfg
   * @param {() => any}                         cfg.getViewer   devuelve el CadViewer (puente) o null.
   * @param {() => object[]}                    cfg.getTargets  grupos THREE a intersectar en el pointerdown.
   * @param {(handle:string, entity:object) => object|null} cfg.getContext
   *        negocio del handle → { vertices, totalLength, direction, directionLocked, appliedMeters }
   *        (o null para ignorar el gesto: capa no trackeable / oculta / sin ancho).
   * @param {(g:object) => void}                cfg.onGesture   recibe { phase, handle, entity, direction,
   *        frac, totalMeters, appliedMeters, deltaMeters, screen:{x,y} }.
   * @param {() => boolean}                     [cfg.isActive]  si devuelve false el controller ignora (ej. modo pan).
   */
  constructor({ getViewer, getTargets, getContext, onGesture, isActive }) {
    this._getViewer  = getViewer
    this._getTargets = getTargets
    this._getContext = getContext
    this._onGesture  = onGesture
    this._isActive   = isActive || (() => true)

    this._raycaster = null
    this._pointer   = null
    this._plane     = null
    this._canvas    = null
    this._dxf       = null

    this._downHandler   = null
    this._moveHandler   = null
    this._upHandler     = null
    this._cancelHandler = null

    // Sesión del gesto en curso (press→release). Null cuando no hay press activo.
    this._session = null
    // Selección persistente del punto de inicio por handle (sobrevive entre un
    // click de selección y el arrastre siguiente). { handle, direction } | null.
    this._selection = null

    this._THRESH = 5 // px: umbral para distinguir click de arrastre.
  }

  /** Engancha pointerdown (canvas) + move/up (window) en CAPTURA (idempotente). */
  bind() {
    if (this._downHandler) return
    const dxf = this._getViewer?.()?.GetDxfViewer?.()
    const canvas = dxf?.GetCanvas?.()
    if (!canvas) return

    this._dxf       = dxf
    this._canvas    = canvas
    this._raycaster = new THREE.Raycaster()
    this._pointer   = new THREE.Vector2()
    this._plane     = new THREE.Plane()

    // pointerdown en el CANVAS (captura), igual que el controlador de hover (patrón
    // probado que recibe los eventos del visor). move/up en window-captura para no
    // perder el arrastre si el puntero sale del canvas. El pan de OrbitControls se
    // neutraliza desde el host (controls.enablePan=false mientras está armado), así
    // que no dependemos de ganar la carrera del evento.
    this._downHandler   = (e) => this._OnDown(e)
    this._moveHandler   = (e) => this._OnMove(e)
    this._upHandler     = (e) => this._OnUp(e)
    this._cancelHandler = (e) => this._OnUp(e)
    // Todo en el canvas (captura). En el pointerdown capturamos el puntero
    // (setPointerCapture) para que move/up se dirijan SIEMPRE al canvas aunque el
    // cursor salga de él — así el arrastre no se pierde (mismo patrón que OrbitControls).
    canvas.addEventListener('pointerdown',   this._downHandler,   true)
    canvas.addEventListener('pointermove',   this._moveHandler,   true)
    canvas.addEventListener('pointerup',     this._upHandler,     true)
    canvas.addEventListener('pointercancel', this._cancelHandler, true)
  }

  /** Desengancha listeners y libera recursos. */
  unbind() {
    if (this._canvas && this._downHandler) {
      try {
        this._canvas.removeEventListener('pointerdown',   this._downHandler,   true)
        this._canvas.removeEventListener('pointermove',   this._moveHandler,   true)
        this._canvas.removeEventListener('pointerup',     this._upHandler,     true)
        this._canvas.removeEventListener('pointercancel', this._cancelHandler, true)
      } catch (e) { /* canvas ya removido */ }
    }
    this._canvas      = null
    this._dxf         = null
    this._raycaster   = null
    this._pointer     = null
    this._plane       = null
    this._downHandler   = null
    this._moveHandler   = null
    this._upHandler     = null
    this._cancelHandler = null
    this._session       = null
    this._selection     = null
  }

  /** Limpia la selección de inicio (p. ej. al cambiar de capa activa o cancelar). */
  clearSelection() {
    this._selection = null
  }

  /**
   * Aborta el gesto en curso (si lo hay): descarta la sesión y libera la captura
   * del puntero, de modo que el pointerup posterior NO dispare commit/popover.
   * Lo usa el host al cancelar (Escape / desarmar) a mitad de un arrastre.
   */
  cancelGesture() {
    const s = this._session
    this._session = null
    if (s && s.pointerId != null) {
      try { this._canvas && this._canvas.releasePointerCapture(s.pointerId) } catch (e) { /* no-op */ }
    }
  }

  /**
   * Fija explícitamente el punto de inicio de un handle (al pulsar el marcador A/B
   * de la UI). Equivale a un click de selección: el siguiente arrastre marcará en
   * ese sentido. No tiene efecto si el sentido está bloqueado por avance previo.
   */
  selectStart(handle, direction) {
    this._selection = { handle: String(handle), direction }
  }

  // ── internos ───────────────────────────────────────────────────────────────

  _OnDown(event) {
    if (!this._isActive() || event.button !== 0) return
    // El puntero debe caer DENTRO del canvas del visor (robusto frente a capas
    // superpuestas: no dependemos de que event.target sea exactamente el canvas).
    if (!this._canvas) return
    const r = this._canvas.getBoundingClientRect()
    if (!r.width || !r.height) return
    if (event.clientX < r.left || event.clientX > r.right ||
        event.clientY < r.top  || event.clientY > r.bottom) return
    const camera = this._dxf?.GetCamera?.()
    if (!camera) return
    const targets = this._getTargets?.() || []
    const picked = pickHandleAtPointer(this._raycaster, this._pointer, camera, event, this._canvas, targets)
    if (!picked || !picked.handle) return   // fuera de segmento → dejamos panear

    const ctx = this._getContext?.(picked.handle, picked.entity)
    if (!ctx || !Array.isArray(ctx.vertices) || ctx.vertices.length < 2) return

    // Punto del press en marco escena (z del plano de los hitboxes).
    const down = this._PointerToScene(event)
    if (!down) return

    const origin = this._dxf?.GetOrigin?.() || { x: 0, y: 0 }
    const sceneVertices = ctx.vertices.map(v => ({ x: v.x - origin.x, y: v.y - origin.y }))

    this._session = {
      handle:          picked.handle,
      entity:          picked.entity,
      sceneVertices,
      totalLength:     ctx.totalLength || 0,
      directionLocked: !!ctx.directionLocked,
      lockedDirection: ctx.direction || 'l2r',
      appliedMeters:   Math.max(0, ctx.appliedMeters || 0),
      downScene:       down,
      downClientX:     event.clientX,
      downClientY:     event.clientY,
      moved:           false,
      direction:       null,
      pointerId:       event.pointerId,
    }

    // El press cayó sobre un segmento trackeable: bloqueamos el pan de OrbitControls
    // y CAPTURAMOS el puntero para que move/up lleguen al canvas durante el arrastre.
    event.stopPropagation()
    try { this._canvas.setPointerCapture(event.pointerId) } catch (e) { /* no-op */ }
  }

  _OnMove(event) {
    const s = this._session
    if (!s) return

    if (!s.moved) {
      const dx = event.clientX - s.downClientX
      const dy = event.clientY - s.downClientY
      if (dx * dx + dy * dy <= this._THRESH * this._THRESH) return
      s.moved = true
      // Al confirmarse el arrastre, fijamos el sentido una sola vez.
      s.direction = s.directionLocked
        ? s.lockedDirection
        : (this._selection && String(this._selection.handle) === String(s.handle)
            ? this._selection.direction
            : nearestEnd(s.downScene, s.sceneVertices))
    }

    event.stopPropagation()

    const p = this._PointerToScene(event)
    if (!p) return
    const frac = projectPointToArcFrac(p, s.sceneVertices, s.direction)
    const totalMeters = Math.max(s.appliedMeters, arcFracToMeters(frac, s.totalLength))
    const deltaMeters = Math.max(0, totalMeters - s.appliedMeters)

    this._onGesture?.({
      phase:         'preview',
      handle:        s.handle,
      entity:        s.entity,
      direction:     s.direction,
      frac:          this._MetersToFrac(totalMeters, s.totalLength),
      totalMeters,
      appliedMeters: s.appliedMeters,
      deltaMeters,
      screen:        { x: event.clientX, y: event.clientY },
    })
  }

  _OnUp(event) {
    const s = this._session
    if (!s) return
    this._session = null
    if (s.pointerId != null) {
      try { this._canvas.releasePointerCapture(s.pointerId) } catch (e) { /* no-op */ }
    }
    // Click robusto: por distancia real down→up (el setPointerCapture puede
    // disparar micro-movimientos que marcarían `moved` en falso).
    const ux = event.clientX - s.downClientX
    const uy = event.clientY - s.downClientY
    const isClick = !s.moved || (ux * ux + uy * uy) <= this._THRESH * this._THRESH

    if (isClick) {
      // Click sin arrastre → selecciona el punto de inicio (si no está bloqueado).
      if (!s.directionLocked) {
        const direction = nearestEnd(s.downScene, s.sceneVertices)
        this._selection = { handle: s.handle, direction }
        this._onGesture?.({ phase: 'select', handle: s.handle, entity: s.entity, direction,
          frac: 0, totalMeters: s.appliedMeters, appliedMeters: s.appliedMeters, deltaMeters: 0,
          screen: { x: event.clientX, y: event.clientY } })
      }
      return
    }

    event.stopPropagation()

    const p = this._PointerToScene(event)
    const frac = p ? projectPointToArcFrac(p, s.sceneVertices, s.direction) : 0
    const totalMeters = Math.max(s.appliedMeters, arcFracToMeters(frac, s.totalLength))
    const deltaMeters = Math.max(0, totalMeters - s.appliedMeters)

    // Guardamos el sentido elegido para seguir marcando sobre el mismo handle.
    this._selection = { handle: s.handle, direction: s.direction }

    // Arrastre con avance → commit (el host abrirá el popover de estado/fecha).
    this._onGesture?.({
      phase:         deltaMeters > 1e-4 ? 'commit' : 'cancel',
      handle:        s.handle,
      entity:        s.entity,
      direction:     s.direction,
      frac:          this._MetersToFrac(totalMeters, s.totalLength),
      totalMeters,
      appliedMeters: s.appliedMeters,
      deltaMeters,
      screen:        { x: event.clientX, y: event.clientY },
    })
  }

  /** Proyecta las coords de cliente del evento sobre el plano de los hitboxes (marco escena). */
  _PointerToScene(event) {
    const camera = this._dxf?.GetCamera?.()
    const canvas = this._canvas
    if (!camera || !canvas) return null
    const rect = canvas.getBoundingClientRect()
    if (!rect.width || !rect.height) return null
    this._pointer.x = ((event.clientX - rect.left) / rect.width)  * 2 - 1
    this._pointer.y = -((event.clientY - rect.top)  / rect.height) * 2 + 1
    this._raycaster.setFromCamera(this._pointer, camera)
    // Los hitboxes del overlay viven en z≈0.05; proyectamos sobre ese plano XY para
    // obtener un punto mundo aunque el puntero salga del segmento (el project clampa).
    this._plane.set(new THREE.Vector3(0, 0, 1), -0.05)
    const target = new THREE.Vector3()
    const hit = this._raycaster.ray.intersectPlane(this._plane, target)
    return hit ? { x: target.x, y: target.y } : null
  }

  _MetersToFrac(meters, totalLength) {
    if (!totalLength || totalLength <= 0) return 0
    return Math.max(0, Math.min(meters / totalLength, 1))
  }
}
